import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { Printer, Calculator, ArrowLeft, FileText, Loader2, AlertCircle } from 'lucide-react';

/**
 * Standalone Payroll Check & Stub Printer.
 *
 * NOT tied to MedCare's internal payroll/accounting. Enter any company
 * + payee + earnings; tax engine computes federal (Pub 15-T 2025),
 * Social Security (6.2% to $176,100), Medicare (1.45% + 0.9% over
 * $200K YTD), state (flat-rate lookup or manual override), and local.
 * Generates a printable Check + Pay-Stub PDF.
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

const defaultForm = {
  company: {
    name: '', address1: '', address2: '', city: '', state: '', zip: '',
    phone: '', ein: '', bank_name: '', bank_routing: '', bank_account: '',
  },
  payee: {
    name: '', address1: '', address2: '', city: '', state: '', zip: '',
    ssn_last4: '', employee_id: '',
  },
  pay_period: 'biweekly',
  pay_date: new Date().toISOString().slice(0, 10),
  period_start: '',
  period_end: '',
  hours_worked: '',
  hourly_rate: '',
  overtime_hours: '',
  overtime_rate: '',
  salary_amount: '',
  other_earnings: '',
  other_earnings_label: 'Other Earnings',
  filing_status: 'single',
  fed_extra_withholding: '',
  state_code: 'TN',
  state_rate_override: '',
  local_tax_rate: '',
  health_insurance: '',
  retirement_401k_pct: '',
  other_pretax: '',
  other_pretax_label: 'Other Pre-Tax',
  other_posttax: '',
  other_posttax_label: 'Other Post-Tax',
  ytd_gross: '',
  ytd_ss_wages: '',
  check_number: '',
  memo: '',
  check_label: 'CHECK',
  custom_footer_statement: '',
  signature_text: '',
  signature_image_b64: '',
};

const STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

const num = (v) => {
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
};

const buildPayload = (f) => ({
  ...f,
  hours_worked: num(f.hours_worked),
  hourly_rate: num(f.hourly_rate),
  overtime_hours: num(f.overtime_hours),
  overtime_rate: num(f.overtime_rate),
  salary_amount: num(f.salary_amount),
  other_earnings: num(f.other_earnings),
  fed_extra_withholding: num(f.fed_extra_withholding),
  state_rate_override: f.state_rate_override === '' ? null : num(f.state_rate_override) / 100,
  local_tax_rate: num(f.local_tax_rate),
  health_insurance: num(f.health_insurance),
  retirement_401k_pct: num(f.retirement_401k_pct),
  other_pretax: num(f.other_pretax),
  other_posttax: num(f.other_posttax),
  ytd_gross: num(f.ytd_gross),
  ytd_ss_wages: num(f.ytd_ss_wages),
});

const PayrollCheckPrinter = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState(defaultForm);
  const [calc, setCalc] = useState(null);
  const [error, setError] = useState('');
  const [busyPreview, setBusyPreview] = useState(false);
  const [busyPdf, setBusyPdf] = useState(false);

  const set = (path, val) => {
    const [a, b] = path.split('.');
    setForm((p) => b ? ({ ...p, [a]: { ...p[a], [b]: val } }) : ({ ...p, [a]: val }));
  };

  const canSubmit = useMemo(() => (
    form.company.name && form.payee.name &&
    (num(form.hours_worked) > 0 || num(form.salary_amount) > 0 || num(form.other_earnings) > 0)
  ), [form]);

  const runPreview = async () => {
    setError(''); setBusyPreview(true);
    try {
      const r = await api.post('/check-printer/preview', buildPayload(form));
      setCalc(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not calculate.');
    } finally { setBusyPreview(false); }
  };

  const downloadPdf = async () => {
    setError(''); setBusyPdf(true);
    try {
      const r = await api.post('/check-printer/pdf', buildPayload(form), { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `PayCheck_${form.payee.name.replace(/\s+/g, '_')}_${form.pay_date}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not generate PDF.');
    } finally { setBusyPdf(false); }
  };

  const openPrintPreview = async () => {
    setError(''); setBusyPdf(true);
    try {
      const r = await api.post('/check-printer/pdf', buildPayload(form), { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const w = window.open(url, '_blank');
      if (w) setTimeout(() => { try { w.print(); } catch (_) {} }, 600);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not generate PDF.');
    } finally { setBusyPdf(false); }
  };

  useEffect(() => { /* re-preview on key field change */
    if (canSubmit) runPreview();
    // eslint-disable-next-line
  }, [
    form.hours_worked, form.hourly_rate, form.overtime_hours, form.overtime_rate,
    form.salary_amount, form.other_earnings, form.filing_status, form.pay_period,
    form.state_code, form.state_rate_override, form.local_tax_rate,
    form.health_insurance, form.retirement_401k_pct, form.other_pretax, form.other_posttax,
    form.fed_extra_withholding, form.ytd_gross, form.ytd_ss_wages,
  ]);

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
      {/* Header bar */}
      <div style={{ background: 'linear-gradient(135deg,#0b2545,#13315c)', color: '#fff', padding: '18px 28px' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => navigate(-1)}
            data-testid="payroll-check-back-btn"
            style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)', color: '#fff', padding: '6px 12px', borderRadius: 8, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}
          >
            <ArrowLeft size={14} /> Back
          </button>
          <Calculator size={22} color="#f7d774" />
          <div>
            <h1 style={{ margin: 0, fontSize: 20, color: '#f7d774', letterSpacing: 0.5 }}>Payroll Check &amp; Stub Printer</h1>
            <div style={{ fontSize: 12, color: '#cbd5e1', marginTop: 2 }}>
              Standalone calculator — federal (IRS Pub 15-T 2025), FICA, state &amp; local. Not tied to MedCare accounting.
            </div>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1280, margin: '24px auto', padding: '0 20px', display: 'grid', gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1fr)', gap: 22 }}>
        {/* ----- LEFT: form ----- */}
        <div>
          <Card title="Company / Employer">
            <Row>
              <Field label="Company name *" value={form.company.name} onChange={(v) => set('company.name', v)} testid="company-name" />
              <Field label="EIN" value={form.company.ein} onChange={(v) => set('company.ein', v)} testid="company-ein" />
            </Row>
            <Row>
              <Field label="Address line 1" value={form.company.address1} onChange={(v) => set('company.address1', v)} />
              <Field label="Address line 2" value={form.company.address2} onChange={(v) => set('company.address2', v)} />
            </Row>
            <Row>
              <Field label="City" value={form.company.city} onChange={(v) => set('company.city', v)} />
              <Select label="State" value={form.company.state} onChange={(v) => set('company.state', v)} options={['', ...STATES]} />
              <Field label="ZIP" value={form.company.zip} onChange={(v) => set('company.zip', v)} />
              <Field label="Phone" value={form.company.phone} onChange={(v) => set('company.phone', v)} />
            </Row>
            <Row>
              <Field label="Bank name" value={form.company.bank_name} onChange={(v) => set('company.bank_name', v)} />
              <Field label="Routing #" value={form.company.bank_routing} onChange={(v) => set('company.bank_routing', v)} />
              <Field label="Account #" value={form.company.bank_account} onChange={(v) => set('company.bank_account', v)} />
            </Row>
          </Card>

          <Card title="Payee / Employee">
            <Row>
              <Field label="Payee name *" value={form.payee.name} onChange={(v) => set('payee.name', v)} testid="payee-name" />
              <Field label="Employee ID" value={form.payee.employee_id} onChange={(v) => set('payee.employee_id', v)} />
              <Field label="SSN last 4" value={form.payee.ssn_last4} onChange={(v) => set('payee.ssn_last4', v)} />
            </Row>
            <Row>
              <Field label="Address line 1" value={form.payee.address1} onChange={(v) => set('payee.address1', v)} />
              <Field label="Address line 2" value={form.payee.address2} onChange={(v) => set('payee.address2', v)} />
            </Row>
            <Row>
              <Field label="City" value={form.payee.city} onChange={(v) => set('payee.city', v)} />
              <Select label="State" value={form.payee.state} onChange={(v) => set('payee.state', v)} options={['', ...STATES]} />
              <Field label="ZIP" value={form.payee.zip} onChange={(v) => set('payee.zip', v)} />
            </Row>
          </Card>

          <Card title="Pay Period &amp; Earnings">
            <Row>
              <Select label="Pay period" value={form.pay_period} onChange={(v) => set('pay_period', v)} options={['weekly','biweekly','semimonthly','monthly','quarterly','annual']} />
              <Field label="Pay date" type="date" value={form.pay_date} onChange={(v) => set('pay_date', v)} />
              <Field label="Period start" type="date" value={form.period_start} onChange={(v) => set('period_start', v)} />
              <Field label="Period end" type="date" value={form.period_end} onChange={(v) => set('period_end', v)} />
            </Row>
            <Row>
              <Field label="Hours worked" type="number" value={form.hours_worked} onChange={(v) => set('hours_worked', v)} testid="hours-worked" />
              <Field label="Hourly rate ($)" type="number" value={form.hourly_rate} onChange={(v) => set('hourly_rate', v)} testid="hourly-rate" />
              <Field label="OT hours" type="number" value={form.overtime_hours} onChange={(v) => set('overtime_hours', v)} />
              <Field label="OT rate (blank → 1.5×)" type="number" value={form.overtime_rate} onChange={(v) => set('overtime_rate', v)} />
            </Row>
            <Row>
              <Field label="OR Salary amount this period ($)" type="number" value={form.salary_amount} onChange={(v) => set('salary_amount', v)} testid="salary-amount" />
              <Field label={form.other_earnings_label} type="number" value={form.other_earnings} onChange={(v) => set('other_earnings', v)} />
              <Field label="Other-earnings label" value={form.other_earnings_label} onChange={(v) => set('other_earnings_label', v)} />
            </Row>
          </Card>

          <Card title="Tax Withholding">
            <Row>
              <Select label="Federal filing status" value={form.filing_status} onChange={(v) => set('filing_status', v)} options={[['single','Single'],['married','Married Filing Jointly'],['head_of_household','Head of Household']]} />
              <Field label="Extra federal $ / period" type="number" value={form.fed_extra_withholding} onChange={(v) => set('fed_extra_withholding', v)} />
            </Row>
            <Row>
              <Select label="State (income tax)" value={form.state_code} onChange={(v) => set('state_code', v)} options={STATES} />
              <Field label="State rate override (%)" type="number" value={form.state_rate_override} onChange={(v) => set('state_rate_override', v)} />
              <Field label="Local tax (%)" type="number" value={form.local_tax_rate} onChange={(v) => set('local_tax_rate', v)} />
            </Row>
            <Row>
              <Field label="YTD gross ($)" type="number" value={form.ytd_gross} onChange={(v) => set('ytd_gross', v)} />
              <Field label="YTD SS wages ($)" type="number" value={form.ytd_ss_wages} onChange={(v) => set('ytd_ss_wages', v)} />
            </Row>
          </Card>

          <Card title="Deductions">
            <Row>
              <Field label="Health insurance (pre-tax $)" type="number" value={form.health_insurance} onChange={(v) => set('health_insurance', v)} />
              <Field label="401(k) %  (pre-tax)" type="number" value={form.retirement_401k_pct} onChange={(v) => set('retirement_401k_pct', v)} />
            </Row>
            <Row>
              <Field label="Other pre-tax ($)" type="number" value={form.other_pretax} onChange={(v) => set('other_pretax', v)} />
              <Field label="Other pre-tax label" value={form.other_pretax_label} onChange={(v) => set('other_pretax_label', v)} />
            </Row>
            <Row>
              <Field label="Other post-tax ($)" type="number" value={form.other_posttax} onChange={(v) => set('other_posttax', v)} />
              <Field label="Other post-tax label" value={form.other_posttax_label} onChange={(v) => set('other_posttax_label', v)} />
            </Row>
          </Card>

          <Card title="Check Details">
            <Row>
              <Select label="Document type" value={form.check_label} onChange={(v) => set('check_label', v)} options={['CHECK','PAYROLL CHECK','DRAFT','BANK DRAFT','VOID','CASHIER\u2019S CHECK','MONEY ORDER']} />
              <Field label="Check number" value={form.check_number} onChange={(v) => set('check_number', v)} testid="check-number" />
              <Field label="Memo" value={form.memo} onChange={(v) => set('memo', v)} />
            </Row>
            <Row>
              <Field label="Custom footer statement (replaces default disclaimer)" value={form.custom_footer_statement} onChange={(v) => set('custom_footer_statement', v)} testid="custom-footer" />
            </Row>
            <Row>
              <Field label="Typed signature (script font)" value={form.signature_text} onChange={(v) => set('signature_text', v)} testid="signature-text" />
              <SignatureUpload value={form.signature_image_b64} onChange={(v) => set('signature_image_b64', v)} />
            </Row>
          </Card>
        </div>

        {/* ----- RIGHT: live preview & actions ----- */}
        <div style={{ position: 'sticky', top: 16, alignSelf: 'flex-start' }}>
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20, boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <FileText size={18} color="#0b2545" />
              <h3 style={{ margin: 0, fontSize: 15, color: '#0b2545' }}>Live Calculation</h3>
              {busyPreview && <Loader2 size={14} color="#94a3b8" style={{ animation: 'spin 1s linear infinite' }} />}
            </div>

            {!calc && (
              <div style={{ fontSize: 13, color: '#64748b' }}>
                Enter the company name, payee name, and earnings (hours or salary) — the calculation will populate here.
              </div>
            )}

            {calc && (
              <>
                <RowKV label="Gross Earnings"            value={calc.gross_earnings} bold />
                <RowKV label="— Pre-Tax Deductions"      value={-calc.pretax_total} />
                <RowKV label="Taxable Federal Wages"     value={calc.taxable_federal_wages} muted />
                <Divider />
                <RowKV label="Federal Income Tax"        value={-calc.federal_income_tax} />
                <RowKV label="Social Security (6.2%)"    value={-calc.social_security_tax} />
                <RowKV label="Medicare (1.45%)"          value={-calc.medicare_tax} />
                {calc.additional_medicare_tax > 0 && (
                  <RowKV label="Add'l Medicare (0.9%)"   value={-calc.additional_medicare_tax} />
                )}
                <RowKV label={`${calc.state_code} State Tax (${(calc.state_rate_used*100).toFixed(2)}%)`} value={-calc.state_income_tax} />
                {calc.local_income_tax > 0 && <RowKV label="Local Tax" value={-calc.local_income_tax} />}
                {calc.posttax_total > 0 && <RowKV label="Post-Tax Deductions" value={-calc.posttax_total} />}
                <Divider />
                <RowKV label="Total Taxes"               value={-calc.total_taxes} muted />
                <div data-testid="net-pay-row" style={{ marginTop: 8, padding: '10px 12px', background: 'linear-gradient(135deg,#0b2545,#13315c)', borderRadius: 8, color: '#f7d774', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 800 }}>
                  <span>NET PAY</span>
                  <span style={{ fontSize: 20 }}>${calc.net_pay.toFixed(2)}</span>
                </div>

                <div style={{ marginTop: 14, padding: 10, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 11, color: '#475569', lineHeight: 1.5 }}>
                  {calc.breakdown_notes.map((n, i) => (<div key={i}>• {n}</div>))}
                </div>
              </>
            )}

            {error && (
              <div data-testid="payroll-error" style={{ marginTop: 12, padding: 10, background: '#fee2e2', color: '#991b1b', borderRadius: 8, fontSize: 12, display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                <AlertCircle size={14} /> <span>{error}</span>
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={openPrintPreview}
                disabled={!canSubmit || busyPdf}
                data-testid="print-check-btn"
                style={btn(canSubmit && !busyPdf, '#0b2545', '#f7d774')}
              >
                {busyPdf ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Printer size={14} />} Print Check
              </button>
              <button
                type="button"
                onClick={downloadPdf}
                disabled={!canSubmit || busyPdf}
                data-testid="download-check-btn"
                style={btn(canSubmit && !busyPdf, '#22c55e', '#fff')}
              >
                {busyPdf ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <FileText size={14} />} Download PDF
              </button>
            </div>
            <div style={{ marginTop: 10, fontSize: 10, color: '#94a3b8' }}>
              Federal: IRS Pub 15-T 2025 percentage method · SS cap $176,100 · Medicare add'l 0.9% over $200K YTD ·
              State rates are flat-rate where applicable; graduated states require a manual rate override.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ---------- presentational helpers ---------- */
const SignatureUpload = ({ value, onChange }) => {
  const ref = useState({ current: null })[0];
  const handle = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { alert('Please upload a PNG or JPG signature image.'); return; }
    const reader = new FileReader();
    reader.onload = () => onChange(reader.result || '');
    reader.readAsDataURL(file);
  };
  return (
    <label style={{ display: 'flex', flexDirection: 'column', fontSize: 11, fontWeight: 700, color: '#475569', letterSpacing: 0.3, textTransform: 'uppercase' }}>
      Upload signature image (PNG/JPG)
      <div style={{ marginTop: 4, display: 'flex', gap: 8, alignItems: 'center' }}>
        <input
          type="file"
          accept="image/png,image/jpeg"
          onChange={handle}
          data-testid="signature-upload"
          style={{ flex: 1, fontSize: 11 }}
        />
        {value && (
          <>
            <img src={value} alt="sig" style={{ height: 28, border: '1px solid #e2e8f0', borderRadius: 4, background: '#fff' }} />
            <button type="button" onClick={() => onChange('')} style={{ background: 'transparent', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>Clear</button>
          </>
        )}
      </div>
    </label>
  );
};

const Card = ({ title, children }) => (
  <section style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, marginBottom: 14, boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
    <h3 style={{ margin: '0 0 12px', fontSize: 14, color: '#0b2545', letterSpacing: 0.4, textTransform: 'uppercase' }}>{title}</h3>
    {children}
  </section>
);
const Row = ({ children }) => (
  <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Array.isArray(children) ? children.length : 1}, minmax(0, 1fr))`, gap: 10, marginBottom: 10 }}>
    {children}
  </div>
);
const Field = ({ label, value, onChange, type = 'text', testid }) => (
  <label style={{ display: 'flex', flexDirection: 'column', fontSize: 11, fontWeight: 700, color: '#475569', letterSpacing: 0.3, textTransform: 'uppercase' }}>
    {label}
    <input
      type={type}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      style={{ marginTop: 4, padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, color: '#0f172a', background: '#fff', fontWeight: 500 }}
    />
  </label>
);
const Select = ({ label, value, onChange, options }) => (
  <label style={{ display: 'flex', flexDirection: 'column', fontSize: 11, fontWeight: 700, color: '#475569', letterSpacing: 0.3, textTransform: 'uppercase' }}>
    {label}
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{ marginTop: 4, padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, background: '#fff', color: '#0f172a', fontWeight: 500 }}
    >
      {options.map((o) => {
        const [v, l] = Array.isArray(o) ? o : [o, o];
        return <option key={v} value={v}>{l || '—'}</option>;
      })}
    </select>
  </label>
);
const RowKV = ({ label, value, bold = false, muted = false }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px dashed #e2e8f0', fontSize: 13, fontWeight: bold ? 700 : 500, color: muted ? '#64748b' : '#0f172a' }}>
    <span>{label}</span>
    <span>${Math.abs(value).toFixed(2)}{value < 0 ? '' : ''}</span>
  </div>
);
const Divider = () => <div style={{ height: 6 }} />;
const btn = (enabled, bg, fg) => ({
  flex: 1,
  minWidth: 130,
  padding: '10px 14px',
  background: enabled ? bg : '#94a3b8',
  color: fg,
  border: 'none',
  borderRadius: 10,
  fontSize: 13,
  fontWeight: 800,
  letterSpacing: 0.3,
  cursor: enabled ? 'pointer' : 'not-allowed',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
});

export default PayrollCheckPrinter;
